// import dependencies ===>
import React, { useEffect, useState } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
// import assets and scss files ===>
import "./printRxTemplate.scss";
// import project files ===>
import Loader from "../UI Component/Loader/Loader";
import PrintRxTemplateHeaderView from "./PrintRxTemplateHeaderView";
import ERXPdfViewConatiner from "../ERXViewContainer/ERXPdfViewContainer";
import PDFViewForC2C from "../ERXViewContainer/ERXPdfViewContainer/PDFViewForC2C";
// import constants files ===>
import {
  GET_PRINT_RX_TEMPLATE,
  GET_USER_SETTINGS,
} from "./constants/apiConstants";
import { c2cGetApiHandler, c2cPostApiHandler } from "../../Util/c2cApiHandler";
import {
  convertToBooleanHelper,
  getDoctorDetails,
  getDoctorFullName,
  getDrugHeaders,
  getFormatedDate,
  getPatientAge,
  getSortedData,
  handleDisplayLeftContent,
} from "../ERXViewContainer/ERXPdfViewContainer/ERXPdfHelper/helper";
import { LOCALSTORAGEKEY } from "../../Util/Helper/localStorageUtils";
import { ERX_PREVIEW_SETTINGS } from "../ERXViewContainer/ERXConstants/constants";

const PrintRxTemplate = (props) => {
  const { appointmentData, handleClose } = props;

  const tokenId = localStorage.getItem(LOCALSTORAGEKEY.ID_TOKEN);
  const userId = localStorage.getItem(LOCALSTORAGEKEY.USER_ID);
  const clinicId = localStorage.getItem(LOCALSTORAGEKEY.CLINIC_ID);

  const [loader, setLoader] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [tempBlob, setTempBlob] = useState("");
  const [blobList, setBlobList] = useState([]);
  const [pdfBlob, setPdfBlob] = useState("");
  const [docs, setDocs] = useState("");
  const [erxPdfData, setErxPdfData] = useState({});
  const [pdfMainContent, setPdfMainContent] = useState({});
  const [langCode, setLangCode] = useState("");
  const [includeInErx, setIncludeInErx] = useState({
    Vitals: true,
    Diagnosis: true,
    PatientExamination: true,
  });
  const [settingsApiCalled, setSettingsApiCalled] = useState(false);

  const alignmentOptions = JSON.parse(JSON.stringify(ERX_PREVIEW_SETTINGS));

  // Lifecycle Methods ===>

  useEffect(() => {
    setLoader(true);
    getUserSettings();
  }, []);

  useEffect(() => {
    if (settingsApiCalled) {
      getPrescriptionForPrintRx();
    }
  }, [settingsApiCalled]);

  useEffect(() => {
    if (erxPdfData && Object.keys(erxPdfData)?.length !== 0) {
      handlePdfMainContent();
    }
  }, [erxPdfData]);

  useEffect(() => {
    setPdfMainContent({ ...erxPdfData, showPdf: false });
  }, [langCode]);

  useEffect(() => {
    if (tempBlob && pdfMainContent?.showPdf) {
      //getting multiple blob
      let list = [...blobList];
      list.push(tempBlob);
      setBlobList(list);
    }
  }, [tempBlob, pdfMainContent]);

  useEffect(() => {
    if (blobList?.length !== 0) {
      //filtering blob based on the size (copleted data is rendered)
      const final_blob = blobList.reduce((max, curren) =>
        max.size > curren.size ? max : curren
      );
      setPdfBlob(final_blob);
    }
  }, [blobList]);

  useEffect(() => {
    if (pdfBlob) {
      let reader = new FileReader();
      reader.readAsDataURL(tempBlob);
      reader.onloadend = () => {
        let base64data = reader.result;
        setDocs([
          {
            uri: `${base64data}`,
          },
        ]);
      };
    }
  }, [pdfBlob]);

  // <===

  // Helper Methods ===>

  const handlePdfMainContent = async () => {
    let data = JSON.parse(JSON.stringify(erxPdfData));
    data["showDoctorDetails"] = handleShowDoctorDetails();
    data["doctorFullName"] = getDoctorFullName(data?.doctor_profile_details);
    if (data?.patient_age) {
      data["display_age"] = getPatientAge(data?.patient_age);
    }
    if (data?.appointment_date) {
      data.appointment_date = getFormatedDate(data?.appointment_date);
    }
    if (data?.follow_up_date) {
      data.follow_up_date = getFormatedDate(data?.follow_up_date);
    }
    let pdfAlignmentOrder = {},
      pdfLanguageSlug;

    let options = data?.alignment_options ?? {};
    alignmentOptions.forEach((ele) => {
      pdfAlignmentOrder[ele.name] = {
        sort: options[ele.name]?.sort ?? ele.sort,
        alignment: options[ele.name]?.alignment ?? ele.alignment,
      };
    });
    data["sortedData"] = await getSortedData(
      data,
      data.includeInErx,
      pdfAlignmentOrder,
      pdfLanguageSlug,
      true
    );
    data["displayLeftContent"] = await handleDisplayLeftContent(
      data["sortedData"]
    );
    data["drugHeader"] = await getDrugHeaders(langCode);
    data["showPdf"] = true;

    setPdfMainContent({ ...data });
  };

  const handleShowDoctorDetails = () => {
    return getDoctorDetails(
      erxPdfData?.clinic_id,
      erxPdfData?.doctor_hospital_details[0]?.enable_custom_header,
      erxPdfData?.doctor_profile_details?.custom_margins_enabled
    )
      ? true
      : false;
  };

  // <===

  // API Calls ===>

  const getUserSettings = () => {
    let params = {
      section_type: "ePrescriptions",
      clinic_id: clinicId ? clinicId : userId,
      user_id: appointmentData?.doctor_id,
    };

    c2cGetApiHandler(GET_USER_SETTINGS, params).then(([res, error]) => {
      if (!error && res?.data?.statusCode === 200) {
        if (Object.keys(res?.data?.data[0]?.filter_options).length !== 0) {
          let data = res?.data?.data[0]?.filter_options;
          let settings = {};
          Object.keys(data).forEach((ele) => {
            let include =
              ele?.toLowerCase() === "vitals" ? data[ele].includeInErx : false;
            settings[ele] = include ?? false;
          });

          setIncludeInErx({ ...settings });
          setSettingsApiCalled(true);
        } else {
          setSettingsApiCalled(true);
        }
      } else {
        setSettingsApiCalled(true);
      }
    });
  };

  const getPrescriptionForPrintRx = () => {
    let requestPayload = {
      appointment_id: appointmentData?.appointment_id,
      includeInErx: includeInErx,
      "authorizer-principal-id": {
        user_id: tokenId,
        user_type: "2",
        agent: "web",
      },
    };
    const resData = c2cPostApiHandler(GET_PRINT_RX_TEMPLATE, requestPayload);
    resData.then(([res, err]) => {
      if (!err && res?.data?.statusCode === 200) {
        let convertedData = convertToBooleanHelper(res?.data?.data);
        if (convertedData?.doctor_profile_details?.custom_margins_enabled) {
          let leftMargin =
            convertedData?.doctor_profile_details?.custom_margin_dimensions
              ?.rxLeft ?? 0;
          let rightMargin =
            convertedData?.doctor_profile_details?.custom_margin_dimensions
              ?.rxRight ?? 0;

          let sumOfLeftRight = Number(leftMargin) + Number(rightMargin);
          convertedData["hideLeftRight"] = sumOfLeftRight > 4;
        } else {
          convertedData["hideLeftRight"] = false;
        }

        setLangCode(convertedData?.langCode);
        setErxPdfData({ ...convertedData });
        setLoader(false);
      } else {
        setErxPdfData({});
        setLoader(false);
      }
    });
  };

  // <===

  // Actions ===>

  const printPDF = () => {
    if (window?.innerWidth < 768) {
      window.open(pdfBlobUrl, "PRINT", "height=400,width=600");
    } else {
      const iframe = document.createElement("iframe"); //load content in an iframe to print later
      document.body.appendChild(iframe);

      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      iframe.onload = function () {
        setTimeout(function () {
          iframe.focus();
          iframe.contentWindow.print();
        }, 1);
      };
    }
  };

  const downloadFile = () => {
    let link = document.createElement("a");
    link.href = pdfBlobUrl;
    link.download = "Prescription for Your Connect2Clinic appointment";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // <===

  return (
    <div className="print-rx-template-container">
      {loader && <Loader />}
      <PrintRxTemplateHeaderView
        printPDF={printPDF}
        downloadFile={downloadFile}
        handleClose={handleClose}
      />
      <div className="print-rx-template-content">
        {window?.innerWidth > 768 && (
          <ERXPdfViewConatiner
            erxPdfData={erxPdfData}
            lang={langCode}
            alignmentOptions={alignmentOptions}
            pdfMainContent={pdfMainContent}
          />
        )}
        {window?.innerWidth < 768 && docs?.length > 0 && (
          <DocViewer
            documents={docs}
            pluginRenderers={DocViewerRenderers}
            style={{
              width: "100%",
              //  height: 800,
            }}
            config={{
              header: {
                disableHeader: false,
                disableFileName: false,
                retainURLParams: false,
              },
            }}
          />
        )}
      </div>

      <PDFDownloadLink
        document={
          <PDFViewForC2C
            erxPdfData={erxPdfData}
            lang={langCode}
            pdfMainContent={pdfMainContent}
          />
        }
        fileName={`Prescription for Your Connect2Clinic appointment`}
      >
        {({ loading, url, blob }) =>
          loading ? (
            <></>
          ) : (
            <>
              {" "}
              {setPdfBlobUrl(url)}
              {setTempBlob(blob)}
            </>
          )
        }
      </PDFDownloadLink>
    </div>
  );
};

export default PrintRxTemplate;
